import { useQuery } from '@tanstack/react-query'
import { v4 as clientUuid } from 'uuid'
import type {
  Client,
  FileGeographyFeatureCollection,
  GeographyLayerData,
} from '~/models'

type GeographiesResponse = {
  geographies: GeographyLayerData[]
}

export function useGeographies(client: Client) {
  const geographiesQuery = useQuery({
    queryKey: ['geographies', client.subdomain],
    queryFn: async () => {
      const response = await fetch('/api/geographies')
      return response.json() as Promise<GeographiesResponse>
    },
    refetchOnWindowFocus: false,
    enabled: client.layers_enabled,
  })

  const fileGeographiesQuery = useQuery({
    queryKey: ['fileGeographies', client.subdomain],
    queryFn: async () => {
      const promises = (client.geojson_layers || []).map(async (file) => {
        const response = await fetch(file)
        return response.json()
      })
      const results = (await Promise.all(promises)).filter(
        (r) => r.properties && r.properties.id && r.properties.name
      ) as FileGeographyFeatureCollection[]

      return results.map((featureCollection) => {
        const isLineStringOnly = featureCollection.features.every(
          (feature) => feature.geometry.type === 'LineString'
        )
        return {
          ...featureCollection,
          properties: {
            ...featureCollection.properties,
            disableHover: isLineStringOnly,
          },
          features: featureCollection.features.map((feature) => {
            return {
              ...feature,
              properties: {
                ...feature.properties,
                id: clientUuid(),
              },
            }
          }),
        }
      })
    },
    refetchOnWindowFocus: false,
    enabled:
      client.layers_enabled &&
      client.geojson_layers &&
      client.geojson_layers.length > 0,
  })

  return {
    geographies: geographiesQuery.data?.geographies ?? [],
    fileGeographies: fileGeographiesQuery.data ?? [],
    isLoading: geographiesQuery.isLoading || fileGeographiesQuery.isLoading,
  }
}
